
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { Logo } from '../ui/Logo';
import { Button } from '../ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sun, Moon, Layout, CreditCard, Info, BookOpen, Globe, Home, ChevronRight, Sparkles, LogIn } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

interface HeaderProps {
    onLogin: () => void;
    onNavigateToDocs: (section?: string) => void;
    onNavigate?: (page: 'landing' | 'docs' | 'solutions' | 'card-landing' | 'about-landing' | 'legal') => void;
}

export const Header: React.FC<HeaderProps> = ({ onLogin, onNavigateToDocs, onNavigate }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { theme, toggleTheme } = useTheme();

    const handleNav = (target: string, type: 'anchor' | 'page' = 'anchor') => {
        if (type === 'page' && onNavigate) {
            onNavigate(target as any);
        } else if (type === 'anchor') {
            if (onNavigate) onNavigate('landing'); 
            setTimeout(() => {
                const element = document.querySelector(target);
                if (element) element.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    };

    const navCards = [
        { id: 'landing', label: 'Home', desc: 'The starting point.', icon: Home, type: 'page' as const },
        { id: 'solutions', label: 'Solutions', desc: 'Enterprise rails.', icon: Layout, type: 'page' as const },
        { id: 'card-landing', label: 'Ghost Card', desc: 'Titanium steel.', icon: CreditCard, type: 'page' as const, highlight: true },
        { id: 'about-landing', label: 'About', desc: 'The mission.', icon: Info, type: 'page' as const },
        { id: 'introduction', label: 'Docs', desc: 'Technical specs.', icon: BookOpen, type: 'docs' as const },
        { id: 'legal', label: 'Legal', desc: 'Regulatory.', icon: Globe, type: 'page' as const },
    ];

    return (
        <>
            <header className="sticky top-0 z-50 bg-white/80 dark:bg-[#0A0B0F]/90 backdrop-blur-xl border-b border-gray-100 dark:border-white/10">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <button onClick={() => handleNav('landing', 'page')} className="focus:outline-none">
                        <Logo />
                    </button>
                    
                    <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600 dark:text-gray-400">
                        <button onClick={() => handleNav('landing', 'page')} className="hover:text-[#0A0B0F] dark:hover:text-white transition-colors font-bold uppercase tracking-wider text-[11px]">Home</button>
                        <button onClick={() => handleNav('solutions', 'page')} className="hover:text-[#0A0B0F] dark:hover:text-white transition-colors font-bold uppercase tracking-wider text-[11px]">Solutions</button>
                        <button onClick={() => handleNav('card-landing', 'page')} className="hover:text-nobel-gold dark:hover:text-nobel-gold transition-colors font-bold uppercase tracking-wider text-[11px]">Ghost Card</button>
                        <button onClick={() => handleNav('about-landing', 'page')} className="hover:text-[#0A0B0F] dark:hover:text-white transition-colors font-bold uppercase tracking-wider text-[11px]">About</button>
                        <button onClick={() => onNavigateToDocs('introduction')} className="hover:text-[#0A0B0F] dark:hover:text-white transition-colors font-bold uppercase tracking-wider text-[11px]">Docs</button>
                    </nav>

                    <div className="hidden md:flex items-center gap-4">
                        <button 
                            onClick={toggleTheme}
                            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-[#0A0B0F] dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                        >
                            <AnimatePresence mode="wait" initial={false}>
                                <motion.div
                                    key={theme}
                                    initial={{ y: -20, opacity: 0, rotate: -90 }}
                                    animate={{ y: 0, opacity: 1, rotate: 0 }}
                                    exit={{ y: 20, opacity: 0, rotate: 90 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
                                </motion.div>
                            </AnimatePresence>
                        </button>
                        <Button onClick={onLogin} variant="secondary" size="md" icon={<LogIn size={16} />}>
                            Sign In
                        </Button>
                    </div>

                    <div className="md:hidden flex items-center gap-4">
                         <button onClick={toggleTheme} className="text-stone-500 dark:text-stone-400">
                            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
                        </button>
                        <button onClick={() => setIsMenuOpen(true)} className="p-2 rounded-md text-gray-600 dark:text-gray-300">
                            <Menu size={24} />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md md:hidden"
                        onClick={() => setIsMenuOpen(false)}
                    >
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: '0%' }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="fixed top-0 right-0 h-full w-full max-w-sm bg-stone-50 dark:bg-[#0A0B0F] shadow-2xl flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center p-6 border-b border-gray-100 dark:border-white/5 bg-white dark:bg-[#0F1014]">
                                <Logo />
                                <button onClick={() => setIsMenuOpen(false)} className="p-2 text-stone-400">
                                    <X size={24} />
                                </button>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto p-6 scroll-smooth">
                                <div className="grid grid-cols-2 gap-3">
                                    {navCards.map((card) => (
                                        <button
                                            key={card.id}
                                            onClick={() => {
                                                if (card.type === 'docs') onNavigateToDocs(card.id);
                                                else handleNav(card.id, 'page');
                                                setIsMenuOpen(false);
                                            }}
                                            className={`relative flex flex-col items-start p-4 rounded-2xl border text-left transition-all group overflow-hidden ${
                                                card.highlight 
                                                ? 'bg-stone-900 border-stone-800 text-white shadow-xl' 
                                                : 'bg-white dark:bg-white/5 border-stone-200 dark:border-white/10 text-[#0A0B0F] dark:text-white'
                                            }`}
                                        >
                                            {card.highlight && (
                                                <div className="absolute top-0 right-0 p-2 opacity-20">
                                                    <Sparkles size={32} className="text-nobel-gold" />
                                                </div>
                                            )}
                                            <div className={`p-2 rounded-lg mb-4 ${card.highlight ? 'bg-white/10 text-nobel-gold' : 'bg-stone-100 dark:bg-white/10 text-stone-500 dark:text-stone-400 group-hover:text-nobel-gold group-hover:bg-nobel-gold/10'}`}>
                                                <card.icon size={20} />
                                            </div>
                                            <div className="font-serif font-bold text-sm">{card.label}</div>
                                            <div className={`text-[10px] mt-1 ${card.highlight ? 'text-stone-400' : 'text-stone-500'}`}>{card.desc}</div>
                                            <div className="mt-4 w-full flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                                                <ChevronRight size={14} className="text-nobel-gold" />
                                            </div>
                                        </button>
                                    ))}
                                </div>

                                <div className="mt-8 space-y-3">
                                    <Button onClick={() => { onLogin(); setIsMenuOpen(false); }} variant="secondary" className="w-full h-14 rounded-2xl text-base shadow-xl" icon={<LogIn size={18} />}>
                                        Sign In
                                    </Button>
                                    <p className="text-center text-[10px] text-stone-400 uppercase tracking-widest font-bold">Encrypted Sovereign OS</p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
